import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { DateFormat } from 'enums/DateFormat';

import { LOCALE, MIN_AGE, MIN_YEAR, TIMEZONE } from './constants';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

function fixFormatForDayjs(format: string) {
  return format.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD');
}

const convertDate = (num: number) => (num < 10 ? `0${num}` : String(num));

export const convertDateFormat = (date: string) => {
  if (!date) return date;

  const newDate = new Date(date);

  return `${newDate.getFullYear()}-${convertDate(newDate.getMonth() + 1)}-${convertDate(newDate.getDate())}`;
};

export const getCurrentDayjsDate = (): Dayjs => {
  return dayjs().tz(TIMEZONE).locale(LOCALE);
};

export const getDateFromFormat = (date: string | Date, format = DateFormat.MonthDayYear): Dayjs => {
  const fixedFormat = fixFormatForDayjs(format);
  let parsed = dayjs(date, fixedFormat, true);

  if (!parsed.isValid()) {
    return parsed;
  }

  parsed = parsed.tz(TIMEZONE, true);

  return parsed.startOf('day');
};

export const isValidDateBeforeNow = (date: string, format = DateFormat.MonthDayYear) => {
  const parsed = getDateFromFormat(date, format);

  return parsed.isValid() && parsed.isBefore(getCurrentDayjsDate());
};

export const isValidDate = (date: string, format = DateFormat.MonthDayYear) => {
  return getDateFromFormat(date, format).isValid();
};

export const getFormattedStringFromDate = (date: Dayjs = getCurrentDayjsDate(), format = DateFormat.MonthDayYear) => {
  return date.format(fixFormatForDayjs(format));
};

export const removeNumberOfYears = (years: number) => {
  return getCurrentDayjsDate().subtract(years, 'year');
};

export const getMinFormattedDate = (format = DateFormat.MonthDayYear) => {
  const d = dayjs.tz(`${MIN_YEAR}-01-01 12:00`, 'YYYY-MM-DD HH:mm', TIMEZONE).startOf('day');

  return d.format(fixFormatForDayjs(format));
};

export const getMinMaxBirthDates = () => {
  const maxBirthDay = getFormattedStringFromDate(removeNumberOfYears(MIN_AGE));
  const minBirthDay = getMinFormattedDate();

  return { minBirthDay: minBirthDay, maxBirthDay: maxBirthDay };
};

export const checkBirthDate = (birthDateString: string): boolean => {
  if (!isValidDate(birthDateString)) return false;

  const { minBirthDay, maxBirthDay } = getMinMaxBirthDates();

  const birthDate = getDateFromFormat(birthDateString);
  const minDate = getDateFromFormat(minBirthDay);
  const maxDate = getDateFromFormat(maxBirthDay);

  return birthDate.isAfter(minDate) && birthDate.isBefore(maxDate);
};

export const addNumberOfDays = (days: number, startDate = getCurrentDayjsDate()) => {
  return startDate.add(days, 'day').tz(TIMEZONE, true);
};
